import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

/** The entity side of consultant single sign-on (ADR-007): the token central handed the browser is given to the API once. */
export const parseSsoLandingSearch = (raw: Record<string, unknown>): { token?: string } => ({ token: typeof raw.token === 'string' && raw.token.length > 0 ? raw.token : undefined });

/** `/sso?token=…` on an entity: the token is handed to the API once; the answer is an ordinary session of this entity. */
export function SsoLandingPage() {
  const { t } = useTranslation();
  const { refresh, publicConfig } = useAuth();
  const navigate = useNavigate();
  const { token } = parseSsoLandingSearch(useSearch({ strict: false }) as Record<string, unknown>);
  const [error, setError] = useState<string | null>(null);
  const tried = useRef(false);
  useEffect(() => {
    if (tried.current) return;
    tried.current = true;
    if (!token) return setError(t('sso.noToken'));
    // The token leaves the address bar before anything else happens: it is single-use, but it should not sit in history.
    window.history.replaceState(null, '', '/sso');
    api.post('/api/auth/sso', { token })
      .then(async () => { await refresh(); void navigate({ to: '/', replace: true }); })
      .catch((e: unknown) => setError(e instanceof ApiError ? t(`sso.errors.${e.code}`, { defaultValue: e.message }) : t('errors.generic')));
  }, [token, refresh, navigate, t]);
  const central = publicConfig?.login.central_url ?? null;
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-4 dark:bg-neutral-950">
      <div className="card w-full max-w-sm p-6 text-center">
        <h1 className="text-lg font-semibold">{t('sso.landingTitle')}</h1>
        {!error && <p className="mt-2 text-sm text-neutral-500">{t('sso.signingIn')}</p>}
        {error && (
          <>
            <p className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-200" role="alert">{error}</p>
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {central && <a href={`${central}/sso/start?entity=${publicConfig?.slug ?? ''}`} className="btn-primary">{t('sso.tryAgain')}</a>}
              <Link to="/login" className="btn-secondary">{t('sso.localLogin')}</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
